import { RefObject, useEffect } from 'react'

/**
 * Fades a scroll container's scrollbar in while it moves and out when it settles.
 * Attach it to an element, or use `FadingScrollArea`, which is a div that has it.
 *
 * Stepped frame by frame in JS on purpose: Chrome paints `::-webkit-scrollbar-*`
 * outside the element pipeline and never runs a CSS transition on it, but it does
 * repaint when a custom property on the container changes.
 */

/** Class that swaps the global thumb colour for the one this file drives. */
const FADE_CLASS = 'cg-fading-scrollbar'
const ALPHA_VAR = '--cg-scrollbar-alpha'
/** How long the box sits still before the bar starts to go. */
const IDLE_MS = 650
/** Fade length, both directions. */
const FADE_MS = 200
/** How close to the bar's edge the pointer counts as reaching for it. Wider than
 *  the 6px bar: a bar you cannot see is a bar you cannot aim at. */
const REACH_PX = 32

/** Gives one element the fade and returns the undo. Imperative so it can be used
 *  from a hook, a component, or an effect holding a node the DS rendered. */
export const attachFadingScrollbar = (box: HTMLElement) => {
	box.classList.add(FADE_CLASS)

	let alpha = 0
	let target = 0
	let frame = 0
	let idle = 0
	let last = 0
	// Pointer is in the reach zone, so the bar holds instead of counting down.
	let reaching = false
	// Cached so a mousemove never reads layout. The reach zone follows whichever bar
	// the box actually has.
	let rect = box.getBoundingClientRect()
	let hasY = box.scrollHeight > box.clientHeight
	let hasX = box.scrollWidth > box.clientWidth

	const measure = () => {
		rect = box.getBoundingClientRect()
		hasY = box.scrollHeight > box.clientHeight
		hasX = box.scrollWidth > box.clientWidth
	}

	const paint = (value: number) => {
		alpha = value
		box.style.setProperty(ALPHA_VAR, value.toFixed(3))
	}

	const step = (now: number) => {
		const move = (last ? now - last : 16) / FADE_MS
		last = now
		paint(
			alpha < target
				? Math.min(target, alpha + move)
				: Math.max(target, alpha - move)
		)
		frame = alpha === target ? 0 : requestAnimationFrame(step)
	}

	const settle = (next: number) => {
		target = next
		// A run already in flight just picks up the new target, so a scroll
		// caught mid-fade turns around instead of restarting.
		if (frame || alpha === target) return
		last = 0
		frame = requestAnimationFrame(step)
	}

	const countdown = () => {
		window.clearTimeout(idle)
		if (!reaching) idle = window.setTimeout(() => settle(0), IDLE_MS)
	}

	const onScroll = () => {
		measure()
		settle(1)
		countdown()
	}

	const onMove = (event: MouseEvent) => {
		const next =
			(hasY && event.clientX > rect.right - REACH_PX) ||
			(hasX && event.clientY > rect.bottom - REACH_PX)
		if (next === reaching) return
		reaching = next
		if (reaching) {
			window.clearTimeout(idle)
			settle(1)
		} else countdown()
	}

	const onLeave = () => {
		if (!reaching) return
		reaching = false
		countdown()
	}

	const onResize = () => {
		measure()
	}

	box.addEventListener('scroll', onScroll, { passive: true })
	box.addEventListener('mousemove', onMove)
	box.addEventListener('mouseleave', onLeave)
	window.addEventListener('resize', onResize)

	return () => {
		box.removeEventListener('scroll', onScroll)
		box.removeEventListener('mousemove', onMove)
		box.removeEventListener('mouseleave', onLeave)
		window.removeEventListener('resize', onResize)
		window.clearTimeout(idle)
		cancelAnimationFrame(frame)
		box.classList.remove(FADE_CLASS)
		box.style.removeProperty(ALPHA_VAR)
	}
}

/** Hook form. Takes a ref for a box we render ourselves, or a selector for one we
 *  do not — a DS component's scroller can only be reached by looking for it. */
export const useFadingScrollbar = (
	target: RefObject<HTMLElement> | string = 'main.app-page-canvas'
) => {
	// A ref object is stable, so the effect keys on the ref itself and reads
	// `.current` when it runs. A selector keys on the string.
	useEffect(() => {
		const box =
			typeof target === 'string'
				? document.querySelector<HTMLElement>(target)
				: target.current
		if (!box) return
		return attachFadingScrollbar(box)
	}, [target])
}
